/**
 * Description: Products section for managing system-product relationships (inline component)
 */

import React, { useState, useEffect } from 'react'
import { Search, Plus, Trash2 } from 'lucide-react'
import { api, Product } from '@services/api'
import { toast } from 'react-toastify'
import ProductForm from '@components/ProductForm'

interface ProductsCatalogProps {
  systemId?: number
  systemName: string
  onProductsChange?: (productIds: number[]) => void
  selectedProductIds?: number[]
}

const ProductsCatalog: React.FC<ProductsCatalogProps> = ({
  systemId,
  systemName,
  onProductsChange,
  selectedProductIds = [],
}) => {
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [linkedProducts, setLinkedProducts] = useState<Product[]>([])
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedProductIds)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)

  useEffect(() => {
    loadProducts()
  }, [])

  const loadProducts = async () => {
    setIsLoading(true)
    try {
      const all = await api.getAllProducts()
      setAllProducts(all)

      // If we have a system ID, also load linked products
      if (systemId) {
        const linked = await api.getSystemProducts(systemId)
        setLinkedProducts(linked)
      }
    } catch (error) {
      toast.error('Failed to load products')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLinkProduct = async (productId: number) => {
    const product = allProducts.find((p) => p.id === productId)
    const productName = product?.product_name || 'Product'

    // Create mode: Update local state and notify parent
    if (!systemId) {
      const newSelectedIds = [...localSelectedIds, productId]
      setLocalSelectedIds(newSelectedIds)
      if (onProductsChange) {
        onProductsChange(newSelectedIds)
      }
      toast.success(`${productName} selected`)
      return
    }

    // Edit mode: Call API to link
    try {
      const system = await api.getSystemById(systemId)
      const systemName = system?.system_name || 'system'

      await api.linkProductToSystem(systemId, productId)
      toast.success(`${productName} linked successfully to ${systemName}`)
      loadProducts()
    } catch (error) {
      toast.error(`Failed to link ${productName} to ${systemName}`)
      console.error(error)
    }
  }

  const handleUnlinkProduct = async (productId: number) => {
    const product = linkedProducts.find((p) => p.id === productId) || allProducts.find((p) => p.id === productId)
    const productName = product?.product_name || 'Product'

    // Create mode: Update local state and notify parent
    if (!systemId) {
      const newSelectedIds = localSelectedIds.filter((id) => id !== productId)
      setLocalSelectedIds(newSelectedIds)
      if (onProductsChange) {
        onProductsChange(newSelectedIds)
      }
      toast.success(`${productName} removed`)
      return
    }

    // Edit mode: Call API to unlink
    try {
      const system = await api.getSystemById(systemId)
      const systemName = system?.system_name || 'system'

      await api.unlinkProductFromSystem(systemId, productId)
      toast.success(`${productName} unlinked successfully from ${systemName}`)
      loadProducts()
    } catch (error) {
      toast.error(`Failed to unlink ${productName} from ${systemName}`)
      console.error(error)
    }
  }

  const handleProductCreated = () => {
    setShowAddProduct(false)
    loadProducts()
  }

  const handleProductSubmit = async (data: Product) => {
    try {
      const result = await api.createProduct(data)
      toast.success(`Product "${data.product_name}" created successfully`)

      // Link the new product to the system if we have a systemId
      if (systemId) {
        await api.linkProductToSystem(systemId, result.id)
      }

      return result
    } catch (error) {
      toast.error(`Failed to create product "${data.product_name}"`)
      throw error
    }
  }

  const isProductLinked = (productId: number) => {
    // In create mode, check local selected IDs
    if (!systemId) {
      return localSelectedIds.includes(productId)
    }
    // In edit mode, check linked products from API
    return linkedProducts.some((p) => p.id === productId)
  }

  const filteredProducts = allProducts
    .filter(
      (product) =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.product_code?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => a.product_name.localeCompare(b.product_name))

  // Get display list for linked/selected products
  const displayLinkedProducts = systemId
    ? linkedProducts
    : allProducts.filter((p) => p.id && localSelectedIds.includes(p.id))

  return (
    <div className="space-y-6">
      {/* Linked Products Section */}
      <div>
        <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-gray-100">
          {systemId ? 'Linked Products' : 'Selected Products'} ({displayLinkedProducts.length})
        </h3>
        {displayLinkedProducts.length > 0 ? (
          <div className="space-y-2">
            {displayLinkedProducts
              .sort((a, b) => a.product_name.localeCompare(b.product_name))
              .map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800/50 dark:bg-green-950/30"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{product.product_name}</p>
                    {product.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{product.description}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {product.product_code && (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {product.product_code}
                        </span>
                      )}
                      {product.category && (
                        <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                          {product.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUnlinkProduct(product.id!)}
                    className="ml-4 cursor-pointer rounded p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                    title="Unlink product"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {systemId ? 'No products linked to this system yet.' : 'No products selected yet.'}
          </p>
        )}
      </div>

      {/* Available Products Section */}
      <div>
        <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-gray-100">Available Products</h3>

        {/* Search */}
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              placeholder="Search products..."
            />
          </div>
        </div>

        {/* Products List */}
        {isLoading ? (
          <p className="py-4 text-center text-gray-500 dark:text-gray-400">Loading products...</p>
        ) : (
          <div className="space-y-2">
            {filteredProducts
              .filter((p) => !isProductLinked(p.id!))
              .map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 hover:bg-gray-100 dark:border-gray-700 dark:bg-white/5 dark:hover:bg-gray-700"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{product.product_name}</p>
                    {product.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{product.description}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {product.product_code && (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {product.product_code}
                        </span>
                      )}
                      {product.category && (
                        <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                          {product.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLinkProduct(product.id!)}
                    className="bg-primary-600 hover:bg-primary-700 ml-4 cursor-pointer rounded px-4 py-2 text-white"
                  >
                    Link
                  </button>
                </div>
              ))}
            {filteredProducts.filter((p) => !isProductLinked(p.id!)).length === 0 && (
              <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No products found matching your search.' : 'All products are already linked.'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Add New Product Section - only visible when editing a system */}
      {systemId && (
        <div>
          <button
            type="button"
            onClick={() => setShowAddProduct(!showAddProduct)}
            className="text-primary-600 hover:text-primary-700 flex cursor-pointer items-center gap-2 font-medium"
          >
            <Plus className="h-5 w-5" />
            Create New Product
          </button>

          {showAddProduct && (
            <div className="mt-3">
              <ProductForm
                inline
                systemId={systemId}
                onSubmit={handleProductSubmit}
                onProductCreated={handleProductCreated}
                onCancel={() => setShowAddProduct(false)}
                autoLink={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ProductsCatalog
